import {
  Component,
  ViewChild,
  TemplateRef,
  inject,
  signal,
  effect,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { ViewContainerRef } from "@angular/core";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { toSignal } from "@angular/core/rxjs-interop";
import { map } from "rxjs";
import { take } from "rxjs/operators";
import { UserFacade } from "state/user.facade";
import { UnitSystem } from "models/Organization";
import { TranslateModule } from "@ngx-translate/core";

import { MatIconModule } from "@angular/material/icon";
import { SelectModalComponent } from "../select-modal/select-modal.component";

@Component({
  selector: "app-unit-system-menu-item",
  templateUrl: "./unit-system-menu-item.component.html",
  styleUrls: ["./unit-system-menu-item.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, MatIconModule, SelectModalComponent],
})
export class UnitSystemMenuItemComponent {
  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly userFacade = inject(UserFacade);

  @ViewChild("unitPortal") unitPortal!: TemplateRef<unknown>;
  private unitOverlayRef?: OverlayRef;

  isUnitDropdownOpen = signal(false);
  selectedUnit = signal<UnitSystem | null>(null);
  chosenUnit = signal<UnitSystem | null>(null);

  readonly isMobile = toSignal(
    this.breakpointObserver.observe(Breakpoints.Handset).pipe(map((r) => r.matches)),
    { initialValue: false }
  );

  protected readonly userUnitSystem = toSignal(this.userFacade.unitSystem$, {
    initialValue: UnitSystem.Metric,
  });

  unitOptions = [{ code: UnitSystem.Metric }, { code: UnitSystem.Imperial }];

  constructor() {
    effect(() => {
      this.selectedUnit.set(this.userUnitSystem());
    });
  }

  getUnitLabel(code: UnitSystem | null): string {
    if (code === UnitSystem.Metric) return "shared.profile_dropdown.unit_metric";
    if (code === UnitSystem.Imperial) return "shared.profile_dropdown.unit_imperial";
    return "shared.profile_dropdown.unit_metric";
  }

  openUnitPortal() {
    if (this.unitOverlayRef?.hasAttached()) return;

    this.isUnitDropdownOpen.set(true);

    this.unitOverlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: "cdk-overlay-dark-backdrop",
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(),
    });

    this.unitOverlayRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(() => this.closeUnitModal());
    this.unitOverlayRef.attach(new TemplatePortal(this.unitPortal, this.vcr));
  }

  closeUnitModal() {
    this.chosenUnit.set(null);
    this.isUnitDropdownOpen.set(false);
    this.disposeUnitOverlay();
  }

  private disposeUnitOverlay() {
    if (this.unitOverlayRef) {
      this.unitOverlayRef.detach();
      this.unitOverlayRef.dispose();
      this.unitOverlayRef = undefined;
    }
  }

  selectUnit(code: string) {
    this.chosenUnit.set(code as UnitSystem);
  }

  cancelUnitSelection() {
    this.closeUnitModal();
  }

  saveUnit() {
    const unit = this.chosenUnit();
    if (!unit) {
      this.closeUnitModal();
      return;
    }

    this.userFacade
      .updateUnitSystem$(unit)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.closeUnitModal();
        },
      });
  }

  closeAll() {
    this.closeUnitModal();
  }
}
